/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertDialogCloseButton} from '@chakra-ui/react'

import PropTypes from 'prop-types'
import {CONFIRMATION_DIALOG_DEFAULT_CONFIG} from '@salesforce/retail-react-app/app/pages/account/constant'
import {useIntl} from 'react-intl'
import {CloseIcon} from '../custom-icons'

/**
 * ConfirmationModal is a React component that renders a modal dialog for confirming user actions.
 * It provides a customizable interface with primary and alternate actions, allowing users to confirm
 * or cancel their decisions. The component leverages internationalization for dynamic message formatting.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.dialogTitle - The title of the dialog, defaulting to a predefined configuration.
 * @param {Object} props.confirmationMessage - The message displayed in the dialog body, defaulting to a predefined configuration.
 * @param {Object} props.primaryActionLabel - The label for the primary action button, defaulting to a predefined configuration.
 * @param {Object} props.primaryActionAriaLabel - The aria-label for the primary action button, defaulting to a predefined configuration.
 * @param {Object} props.alternateActionLabel - The label for the alternate action button, defaulting to a predefined configuration.
 * @param {Object} props.alternateActionAriaLabel - The aria-label for the alternate action button, defaulting to a predefined configuration.
 * @param {boolean} props.hideAlternateAction - A flag to hide the alternate action button, defaulting to false.
 * @param {Function} props.onPrimaryAction - Callback function executed when the primary action is triggered, defaulting to a no-operation function.
 * @param {Function} props.onAlternateAction - Callback function executed when the alternate action is triggered, defaulting to a no-operation function.
 * @param {boolean} props.isOpen - Determines if the modal is open.
 * @param {Function} props.onClose - Callback function executed when the modal is closed.
 * @param {...Object} props - Additional properties passed to the AlertDialog component.
 *
 * @returns {JSX.Element} A JSX element representing the confirmation modal.
 */
const ConfirmationModal = ({
    dialogTitle = CONFIRMATION_DIALOG_DEFAULT_CONFIG.dialogTitle,
    confirmationMessage = CONFIRMATION_DIALOG_DEFAULT_CONFIG.confirmationMessage,
    primaryActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.primaryActionLabel,
    primaryActionAriaLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.primaryActionAriaLabel,
    alternateActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.alternateActionLabel,
    alternateActionAriaLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.alternateActionAriaLabel,
    hideAlternateAction = false,
    onPrimaryAction = noop,
    onAlternateAction = noop,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const [isPrimaryActionLoading, setIsPrimaryActionLoading] = useState(false)
    const handleConfirmClick = async () => {
        if (isPrimaryActionLoading) return

        setIsPrimaryActionLoading(true)

        try {
            await onPrimaryAction()
            props.onClose()
        } finally {
            setIsPrimaryActionLoading(false)
        }
    }

    const handleAlternateActionClick = () => {
        onAlternateAction()
        props.onClose()
    }

    // If we want to format the message in the parent component, we cannot use formatMessage here
    const getFormattedMessage = (message) => {
        return message.defaultMessage ? formatMessage(message) : message
    }

    return (
        <AlertDialog
            isOpen={props.isOpen}
            isCentered
            onClose={handleAlternateActionClick}
            {...props}
            size="small"
        >
            <AlertDialogOverlay />
            <AlertDialogContent>
                <AlertDialogHeader pr={25}>{getFormattedMessage(dialogTitle)}</AlertDialogHeader>
                <AlertDialogCloseButton>
                    <CloseIcon boxSize="14px" />
                </AlertDialogCloseButton>
                <AlertDialogBody>
                    <Text>{getFormattedMessage(confirmationMessage)}</Text>
                </AlertDialogBody>

                <AlertDialogFooter
                    justify="space-between"
                    justifyContent="flex-start"
                    w="full"
                    p="0"
                    flexWrap={['wrap', null, 'nowrap', 'nowrap']}
                    gap={16}
                >
                    {!hideAlternateAction ? (
                        <Button
                            variant="secondary"
                            mr={7}
                            flex={['1 0 100%', null, 'auto']}
                            aria-label={getFormattedMessage(alternateActionAriaLabel)}
                            onClick={handleAlternateActionClick}
                            whiteSpace={['wrap', null, 'nowrap']}
                        >
                            {getFormattedMessage(alternateActionLabel)}
                        </Button>
                    ) : null}
                    <Button
                        variant="primary"
                        flex={['1 0 100%', null, 'auto']}
                        onClick={handleConfirmClick}
                        isLoading={isPrimaryActionLoading}
                        isDisabled={isPrimaryActionLoading}
                        aria-label={getFormattedMessage(primaryActionAriaLabel)}
                        whiteSpace={['wrap', null, 'nowrap']}
                    >
                        {getFormattedMessage(primaryActionLabel)}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

ConfirmationModal.propTypes = {
    /**
     * Prop to check if modal is open
     */
    isOpen: PropTypes.bool.isRequired,
    /**
     * Callback invoked to open the modal
     */
    onOpen: PropTypes.func.isRequired,
    /**
     * Callback invoked to close the modal
     */
    onClose: PropTypes.func.isRequired,
    /**
     * Text to be displayed as modal header
     */
    dialogTitle: PropTypes.object,
    /**
     * Text to display in confirmation modal prompting user to pick an action
     */
    confirmationMessage: PropTypes.object,
    /**
     * Button Label for primary action in confirmation modal
     */
    primaryActionLabel: PropTypes.object,
    /**
     * Button aria Label for primary action
     */
    primaryActionAriaLabel: PropTypes.object,
    /**
     * Button Label for alternate or secondary action in confirmation modal
     */
    alternateActionLabel: PropTypes.object,
    /**
     * Button aria Label for alternate or secondary action in confirmation modal
     */
    alternateActionAriaLabel: PropTypes.object,
    /**
     * Action to execute if user selects primary action
     */
    onPrimaryAction: PropTypes.func,
    /**
     * Action to execute if user selects alternate or secondary action
     */
    onAlternateAction: PropTypes.func,
    /**
     * Flag to hide of show alternative button
     */
    hideAlternateAction: PropTypes.bool
}

export default ConfirmationModal
